import { useEffect, useState, useRef, useCallback } from 'react';
import { useTour } from '@/contexts/TourContext';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function ProductTour() {
  const { tourState, steps, nextStep, previousStep, skipTour, finishTour } = useTour();
  const [spotlightRect, setSpotlightRect] = useState<SpotlightRect | null>(null);
  const [cardPosition, setCardPosition] = useState<{ top: number; left: number } | null>(null);
  const [elementNotFound, setElementNotFound] = useState(false);
  const [isPositioned, setIsPositioned] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<MutationObserver | null>(null);
  const retryCountRef = useRef(0);
  const rafRef = useRef<number | null>(null);

  const currentStepData = steps[tourState.currentStep];
  const isFirstStep = tourState.currentStep === 0;
  const isLastStep = tourState.currentStep === steps.length - 1;

  const calculateCardPosition = useCallback((
    targetRect: DOMRect,
    cardRect: DOMRect,
    position: 'top' | 'bottom' | 'left' | 'right'
  ) => {
    const padding = 16;
    const spotlightPadding = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = 0;
    let left = 0;

    switch (position) {
      case 'top':
        top = targetRect.top - spotlightPadding - cardRect.height - padding;
        left = targetRect.left + targetRect.width / 2 - cardRect.width / 2;
        break;
      case 'bottom':
        top = targetRect.bottom + spotlightPadding + padding;
        left = targetRect.left + targetRect.width / 2 - cardRect.width / 2;
        break;
      case 'left':
        top = targetRect.top + targetRect.height / 2 - cardRect.height / 2;
        left = targetRect.left - spotlightPadding - cardRect.width - padding;
        break;
      case 'right':
        top = targetRect.top + targetRect.height / 2 - cardRect.height / 2;
        left = targetRect.right + spotlightPadding + padding;
        break;
    }

    if (left < padding) left = padding;
    if (left + cardRect.width > viewportWidth - padding) {
      left = viewportWidth - cardRect.width - padding;
    }
    if (top < padding) top = padding;
    if (top + cardRect.height > viewportHeight - padding) {
      top = viewportHeight - cardRect.height - padding;
    }

    return { top, left };
  }, []);

  useEffect(() => {
    if (!tourState.isActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
        case 'Enter':
          if (!isLastStep) {
            e.preventDefault();
            nextStep();
          } else {
            e.preventDefault();
            finishTour();
          }
          break;
        case 'ArrowLeft':
          if (!isFirstStep) {
            e.preventDefault();
            previousStep();
          }
          break;
        case 'Escape':
          e.preventDefault();
          skipTour();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [tourState.isActive, isFirstStep, isLastStep, nextStep, previousStep, skipTour, finishTour]);

  useEffect(() => {
    if (!tourState.isActive || !currentStepData) {
      setSpotlightRect(null);
      setCardPosition(null);
      setElementNotFound(false);
      setIsPositioned(false);
      retryCountRef.current = 0;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return;
    }

    setIsPositioned(false);
    retryCountRef.current = 0;

    const updatePosition = () => {
      const targetElement = document.querySelector(currentStepData.targetSelector);
      
      if (targetElement) {
        const rect = targetElement.getBoundingClientRect();
        const spotlightPadding = 8;
        
        setSpotlightRect({
          top: rect.top - spotlightPadding,
          left: rect.left - spotlightPadding,
          width: rect.width + spotlightPadding * 2,
          height: rect.height + spotlightPadding * 2,
        });
        setElementNotFound(false);
        retryCountRef.current = 0;

        rafRef.current = requestAnimationFrame(() => {
          if (cardRef.current) {
            const cardRect = cardRef.current.getBoundingClientRect();
            const position = currentStepData.position || 'bottom';
            const newCardPosition = calculateCardPosition(rect, cardRect, position);
            setCardPosition(newCardPosition);
            setIsPositioned(true);
          }
        });
      } else {
        retryCountRef.current += 1;
        if (retryCountRef.current < 20) {
          setTimeout(updatePosition, 100);
        } else {
          setElementNotFound(true);
          setSpotlightRect(null);
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          setCardPosition({
            top: viewportHeight / 2 - 150,
            left: viewportWidth / 2 - 200,
          });
          setIsPositioned(true);
        }
      }
    };

    const timeoutId = setTimeout(updatePosition, 50);

    const handleResize = () => {
      updatePosition();
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);

    observerRef.current = new MutationObserver(() => {
      updatePosition();
    });
    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [tourState.isActive, tourState.currentStep, currentStepData, calculateCardPosition]);

  if (!tourState.isActive) {
    return null;
  }

  const handleNext = () => {
    if (isLastStep) {
      finishTour();
    } else {
      nextStep();
    }
  };

  const spotlightBoxShadow = spotlightRect
    ? `0 0 0 9999px rgba(0, 0, 0, 0.75), 0 0 0 4px hsl(var(--primary) / 0.5)`
    : 'none';

  return (
    <>
      {spotlightRect && (
        <div
          className="fixed z-[9998] rounded-lg pointer-events-none transition-all duration-300 ease-out"
          style={{
            top: spotlightRect.top,
            left: spotlightRect.left,
            width: spotlightRect.width,
            height: spotlightRect.height,
            boxShadow: spotlightBoxShadow,
          }}
        />
      )}

      {!spotlightRect && tourState.isActive && (
        <div className="fixed inset-0 z-[9998] bg-black/75 pointer-events-none" />
      )}

      <div
        ref={cardRef}
        className={cn(
          "fixed z-[10000] w-[400px] transition-all duration-300",
          isPositioned ? "opacity-100" : "opacity-0"
        )}
        style={{
          top: cardPosition?.top ?? -9999,
          left: cardPosition?.left ?? -9999,
        }}
      >
        <Card className="shadow-2xl border-2">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <div className="text-xs text-muted-foreground mb-1">
                  Step {tourState.currentStep + 1} of {steps.length}
                </div>
                <CardTitle className="text-lg">{currentStepData?.title}</CardTitle>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 -mt-1 -mr-1"
                onClick={skipTour}
                title="Close tour (Esc)"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="mt-3 flex gap-1">
              {steps.map((_, index) => (
                <div
                  key={index}
                  className={cn(
                    "h-1 flex-1 rounded-full transition-colors",
                    index <= tourState.currentStep ? "bg-primary" : "bg-muted"
                  )}
                />
              ))}
            </div>
          </CardHeader>
          <CardContent className="pb-4">
            {elementNotFound ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {currentStepData?.description}
                </p>
                <div className="p-3 bg-muted rounded-md">
                  <p className="text-sm text-muted-foreground">
                    <strong>Note:</strong> This feature is not currently visible on the screen.
                    {currentStepData?.id === 'edit-booking' && ' Try creating a booking first to see this in action.'}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground leading-relaxed">
                {currentStepData?.description}
              </p>
            )}
          </CardContent>
          <CardFooter className="flex flex-col gap-3 pt-0">
            <div className="flex items-center justify-between gap-2 w-full">
              <Button
                variant="outline"
                size="sm"
                onClick={previousStep}
                disabled={isFirstStep}
                className="gap-1"
                title="Previous step"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Previous
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={skipTour}
                className="text-muted-foreground"
                title="Skip tour"
              >
                Skip Tour
              </Button>
              <Button
                size="sm"
                onClick={handleNext}
                className="gap-1"
                title={isLastStep ? 'Finish tour' : 'Next step'}
              >
                {isLastStep ? 'Finish' : 'Next'}
                {!isLastStep && <ArrowRight className="h-3.5 w-3.5" />}
              </Button>
            </div>
          </CardFooter>
        </Card>
      </div>
    </>
  );
}
